import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Camera, Download, Eye, FileText, RefreshCw, Route, Search, Upload } from 'lucide-react';
import { FadeIn, GlassButton, GlassCard, GlassInput, GlassSelect } from '../../components/ui';
import { downloadDocument, previewDocument } from '../../utils/documentViewer';

const API = '/erp/api/v1';

const modules = [
  { value: 'automatico', label: 'Detectar automaticamente' },
  { value: 'geral', label: 'Geral' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'construtora', label: 'Construtora' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'rh', label: 'RH' },
  { value: 'frota', label: 'Frota' },
  { value: 'estoque', label: 'Estoque' },
];

const typesByModule = {
  automatico: [{ value: 'automatico', label: 'Detectar automaticamente' }],
  geral: [{ value: 'documento', label: 'Documento' }],
  financeiro: [
    { value: 'conta_pagar', label: 'Conta a pagar' },
    { value: 'compra_cartao', label: 'Compra no cartão' },
  ],
  construtora: [
    { value: 'nota_obra', label: 'Nota de obra' },
    { value: 'pedido_obra', label: 'Pedido de obra' },
    { value: 'orcamento_obra', label: 'Orçamento' },
    { value: 'contrato_venda', label: 'Contrato de venda' },
    { value: 'aditivo_contratual', label: 'Aditivo contratual' },
    { value: 'distrato', label: 'Distrato' },
    { value: 'termo_acordo', label: 'Termo de acordo' },
    { value: 'cessao_direitos', label: 'Cessão de direitos' },
    { value: 'cliente_documento', label: 'Documento de cliente' },
    { value: 'comprovante_endereco', label: 'Comprovante de endereço' },
    { value: 'certidao_estado_civil', label: 'Certidão estado civil' },
    { value: 'tabela_vendas', label: 'Tabela de vendas' },
  ],
  juridico: [
    { value: 'juridico_processo', label: 'Processo' },
    { value: 'juridico_cobranca', label: 'Cobrança' },
  ],
  rh: [
    { value: 'funcionario_documento', label: 'Documento funcionário' },
    { value: 'cartao_ponto', label: 'Cartão ponto' },
    { value: 'atestado', label: 'Atestado' },
    { value: 'empreiteira_documento', label: 'Empreiteira' },
  ],
  frota: [
    { value: 'documento_veiculo', label: 'Documento veículo' },
    { value: 'dossie_detran', label: 'Dossiê Detran' },
    { value: 'seguro', label: 'Seguro' },
    { value: 'manutencao', label: 'Manutenção' },
    { value: 'multa', label: 'Multa' },
  ],
  estoque: [{ value: 'equipamento_documento', label: 'Equipamento' }],
};

async function getToken() {
  const token = localStorage.getItem('nexus_token');
  if (!token) {
    window.location.assign('/erp/login');
    return '';
  }
  return token;
}

async function authOnlyHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}` };
}

async function jsonHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const statusClass = (status) => {
  if (status === 'encaminhado') return 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200';
  if (status === 'precisa_ocr') return 'border-amber-400/25 bg-amber-500/10 text-amber-200';
  if (status === 'erro') return 'border-red-400/25 bg-red-500/10 text-red-200';
  return 'border-sky-400/25 bg-sky-500/10 text-sky-200';
};

const fmtDate = (value) => value ? new Date(value).toLocaleString('pt-BR') : '-';
const fmtBytes = (value) => `${Math.round((Number(value || 0) / 1024) * 10) / 10} KB`;
const displayName = (item) => item?.nome_armazenado || item?.nome_original || 'documento';

async function optimizeMobileImage(file) {
  if (!file?.type?.startsWith('image/') || typeof createImageBitmap !== 'function') return file;
  const bitmap = await createImageBitmap(file);
  const longestSide = Math.max(bitmap.width, bitmap.height);
  const scale = Math.min(1, 2400 / longestSide);
  if (scale === 1 && file.size <= 5 * 1024 * 1024) return file;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.84));
  if (!blob || blob.size >= file.size) return file;
  const baseName = (file.name || 'foto').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
}

async function readResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return res.json();
  const text = await res.text();
  return { message: text || `Resposta HTTP ${res.status}` };
}

const entityConfig = (moduleValue, typeValue) => {
  if (moduleValue === 'construtora' && ['contrato_venda', 'aditivo_contratual', 'distrato', 'termo_acordo', 'cessao_direitos', 'cliente_documento', 'comprovante_endereco', 'certidao_estado_civil'].includes(typeValue)) {
    return {
      key: 'construtora_clientes',
      label: 'Cliente',
      endpoint: '/construtora/clientes?search=',
      format: (item) => `${item.nome || 'Cliente'}${item.cpf_cnpj ? ` · ${item.cpf_cnpj}` : ''}`,
    };
  }
  if (moduleValue === 'rh' && ['funcionario_documento', 'cartao_ponto', 'atestado'].includes(typeValue)) {
    return {
      key: 'rh_funcionarios',
      label: 'Funcionário',
      endpoint: '/rh/funcionarios?per_page=200',
      format: (item) => `${item.nome || 'Funcionário'}${item.funcao ? ` · ${item.funcao}` : ''}${item.empresa ? ` · ${item.empresa}` : ''}`,
    };
  }
  if (moduleValue === 'rh' && typeValue === 'empreiteira_documento') {
    return {
      key: 'rh_empreiteira_contratos',
      label: 'Contrato da empreiteira',
      endpoint: '/rh/empreiteiras/contratos',
      format: (item) => `${item.empreiteira || 'Empreiteira'}${item.obra ? ` · ${item.obra}` : ''}${item.cno ? ` · CNO ${item.cno}` : ''}`,
    };
  }
  if (moduleValue === 'frota') {
    return {
      key: 'frota_veiculos',
      label: 'Veículo',
      endpoint: '/frota/veiculos?per_page=200',
      format: (item) => `${item.placa || 'Veículo'} · ${[item.marca, item.modelo].filter(Boolean).join(' ') || 'Sem modelo'}`,
    };
  }
  return null;
};

const contextFieldsFor = (moduleValue, typeValue) => {
  if (moduleValue === 'frota' && typeValue === 'manutencao') {
    return [
      { key: 'data', label: 'Data', type: 'date' },
      { key: 'fornecedor', label: 'Fornecedor' },
      { key: 'valor', label: 'Valor', type: 'number', step: '0.01' },
      { key: 'km_atual', label: 'Km atual', type: 'number' },
      { key: 'km_proxima', label: 'Próxima manutenção (km)', type: 'number' },
      { key: 'descricao', label: 'Descrição' },
    ];
  }
  if (moduleValue === 'frota' && typeValue === 'multa') {
    return [
      { key: 'data_infracao', label: 'Data da infração', type: 'date' },
      { key: 'hora_infracao', label: 'Hora' },
      { key: 'vencimento', label: 'Vencimento', type: 'date' },
      { key: 'valor', label: 'Valor', type: 'number', step: '0.01' },
      { key: 'pontos', label: 'Pontos', type: 'number' },
      { key: 'local', label: 'Local' },
      { key: 'motorista_indicado', label: 'Motorista indicado' },
      { key: 'auto_infracao', label: 'Auto de infração' },
    ];
  }
  if (moduleValue === 'frota' && ['documento_veiculo', 'dossie_detran', 'seguro'].includes(typeValue)) {
    return [
      { key: 'data_documento', label: 'Data do documento', type: 'date' },
      { key: 'ano_referencia', label: 'Ano referência', type: 'number' },
      { key: 'competencia', label: 'Competência' },
    ];
  }
  if (moduleValue === 'rh' && typeValue === 'empreiteira_documento') {
    return [
      { key: 'competencia', label: 'Competência' },
      { key: 'tipo_documento', label: 'Tipo do documento' },
      { key: 'trabalhador_id', label: 'ID do trabalhador' },
      { key: 'valor_deposito', label: 'Valor depósito', type: 'number', step: '0.01' },
      { key: 'data_documento', label: 'Data do documento', type: 'date' },
    ];
  }
  if (moduleValue === 'rh' && ['funcionario_documento', 'cartao_ponto', 'atestado'].includes(typeValue)) {
    return [
      { key: 'data_documento', label: 'Data do documento', type: 'date' },
      { key: 'observacao', label: 'Observação' },
    ];
  }
  if (moduleValue === 'construtora' && ['contrato_venda', 'aditivo_contratual', 'distrato', 'termo_acordo', 'cessao_direitos', 'cliente_documento', 'comprovante_endereco', 'certidao_estado_civil'].includes(typeValue)) {
    return [
      { key: 'unidade_id', label: 'ID da unidade' },
      { key: 'contrato_id', label: 'ID do contrato' },
      { key: 'data_documento', label: 'Data do documento', type: 'date' },
    ];
  }
  if (moduleValue === 'construtora' && ['nota_obra', 'pedido_obra', 'orcamento_obra'].includes(typeValue)) {
    return [
      { key: 'obra_id', label: 'ID da obra' },
      { key: 'etapa_id', label: 'ID da etapa' },
      { key: 'fornecedor', label: 'Fornecedor' },
      { key: 'valor', label: 'Valor', type: 'number', step: '0.01' },
      { key: 'vencimento', label: 'Vencimento', type: 'date' },
    ];
  }
  return [];
};

const contextHint = (moduleValue, typeValue) => {
  if (moduleValue === 'construtora' && ['contrato_venda', 'aditivo_contratual', 'distrato', 'termo_acordo', 'cessao_direitos', 'cliente_documento', 'comprovante_endereco', 'certidao_estado_civil'].includes(typeValue)) {
    return 'Para anexar ao dossiê, informe aqui o ID do cliente. Se preferir, encaminhe depois com cliente_id, unidade_id ou contrato_id no contexto.';
  }
  if (moduleValue === 'construtora' && ['nota_obra', 'pedido_obra', 'orcamento_obra'].includes(typeValue)) {
    return 'Opcional: informe o ID da obra/cadastro relacionado. O arquivo ficará arquivado para revisão operacional.';
  }
  if (moduleValue === 'rh' && ['funcionario_documento', 'cartao_ponto', 'atestado'].includes(typeValue)) {
    return 'Para anexar ao dossiê, informe aqui o ID do funcionário.';
  }
  if (moduleValue === 'rh' && typeValue === 'empreiteira_documento') {
    return 'Para anexar ao dossiê, informe aqui o ID do contrato da empreiteira.';
  }
  if (moduleValue === 'frota') {
    return 'Para anexar ao dossiê, informe aqui o ID do veículo.';
  }
  return 'Opcional';
};

export const DocumentosOCR = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [entitySearch, setEntitySearch] = useState('');
  const [entityOptions, setEntityOptions] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [contexto, setContexto] = useState({});
  const [uploadInfo, setUploadInfo] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [form, setForm] = useState({
    modulo_destino: 'automatico',
    tipo_destino: 'automatico',
    entidade_id: '',
    encaminhar: false,
    captura_mobile: false,
    arquivo: null,
  });

  const typeOptions = useMemo(() => typesByModule[form.modulo_destino] || typesByModule.geral, [form.modulo_destino]);
  const cadastroConfig = useMemo(() => entityConfig(form.modulo_destino, form.tipo_destino), [form.modulo_destino, form.tipo_destino]);
  const contextFields = useMemo(() => contextFieldsFor(form.modulo_destino, form.tipo_destino), [form.modulo_destino, form.tipo_destino]);
  const filteredEntityOptions = useMemo(() => {
    const term = entitySearch.trim().toLowerCase();
    if (!term) return entityOptions;
    return entityOptions.filter((option) => option.label.toLowerCase().includes(term));
  }, [entityOptions, entitySearch]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => [
      item.nome_armazenado,
      item.nome_original,
      item.modulo_destino,
      item.tipo_destino,
      item.status,
      item.texto_preview,
    ].join(' ').toLowerCase().includes(term));
  }, [items, search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authOnlyHeaders();
      const res = await fetch(`${API}/documentos`, { headers });
      const data = await readResponse(res);
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao carregar documentos.');
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Erro ao carregar documentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEntityOptions = useCallback(async () => {
    if (!cadastroConfig) {
      setEntityOptions([]);
      return;
    }
    setLoadingEntities(true);
    try {
      const headers = await authOnlyHeaders();
      const res = await fetch(`${API}${cadastroConfig.endpoint}`, { headers });
      const data = await readResponse(res);
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao carregar cadastros.');
      setEntityOptions((data.items || []).map((item) => ({
        value: item.id,
        label: cadastroConfig.format(item),
      })));
    } catch (err) {
      setEntityOptions([]);
      setError(err.message || 'Erro ao carregar cadastros.');
    } finally {
      setLoadingEntities(false);
    }
  }, [cadastroConfig]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadEntityOptions(); }, [loadEntityOptions]);

  useEffect(() => {
    const action = searchParams.get('acao');
    const modulo = searchParams.get('modulo');
    const tipo = searchParams.get('tipo');
    const documentoId = searchParams.get('documento_id');
    if (!action && !modulo && !tipo && !documentoId) return;
    setForm((current) => {
      const nextModule = modulo || current.modulo_destino;
      const options = typesByModule[nextModule] || typesByModule.geral;
      return {
        ...current,
        modulo_destino: nextModule,
        tipo_destino: tipo || options[0].value,
        encaminhar: action === 'encaminhar' ? true : current.encaminhar,
        entidade_id: searchParams.get('entidade_id') || current.entidade_id,
      };
    });
    if (documentoId) {
      const loadSelectedDocument = async () => {
        try {
          const headers = await authOnlyHeaders();
          const res = await fetch(`${API}/documentos/${documentoId}`, { headers });
          const data = await readResponse(res);
          if (!res.ok) throw new Error(data.message || data.error || 'Erro ao carregar documento.');
          setSelected(data);
          setForm((current) => ({
            ...current,
            modulo_destino: modulo || data.modulo_destino || current.modulo_destino,
            tipo_destino: tipo || data.tipo_destino || current.tipo_destino,
            entidade_id: searchParams.get('entidade_id') || data.entidade_id || current.entidade_id,
            encaminhar: action === 'encaminhar' ? true : current.encaminhar,
          }));
          setContexto(data.contexto || {});
        } catch (err) {
          setError(err.message || 'Erro ao carregar documento.');
        }
      };
      loadSelectedDocument();
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const updateForm = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'modulo_destino') {
        next.tipo_destino = (typesByModule[value] || typesByModule.geral)[0].value;
        next.entidade_id = '';
        setContexto({});
      }
      if (key === 'tipo_destino') {
        next.entidade_id = '';
        setContexto({});
      }
      return next;
    });
  };

  const updateContexto = (key, value) => {
    setContexto((current) => ({ ...current, [key]: value }));
  };

  const selectFile = (file, fromCamera = false) => {
    updateForm('arquivo', file);
    updateForm('captura_mobile', fromCamera);
    setUploadInfo(file ? `Selecionado: ${file.name} (${fmtBytes(file.size)}).` : '');
  };

  const cleanContexto = () => Object.fromEntries(
    Object.entries(contexto).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!form.arquivo) {
      setError('Selecione um arquivo.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authOnlyHeaders();
      const fd = new FormData();
      const fileToUpload = form.captura_mobile ? await optimizeMobileImage(form.arquivo) : form.arquivo;
      if (fileToUpload !== form.arquivo) setUploadInfo(`Foto preparada para envio: ${fmtBytes(form.arquivo.size)} → ${fmtBytes(fileToUpload.size)}.`);
      fd.append('arquivo', fileToUpload);
      if (form.modulo_destino !== 'automatico') fd.append('modulo_destino', form.modulo_destino);
      if (form.tipo_destino !== 'automatico') fd.append('tipo_destino', form.tipo_destino);
      fd.append('entidade_id', form.entidade_id || '');
      fd.append('contexto', JSON.stringify(cleanContexto()));
      fd.append('encaminhar', form.encaminhar ? 'true' : 'false');
      fd.append('captura_mobile', form.captura_mobile ? 'true' : 'false');
      fd.append('origem', form.captura_mobile ? 'web_mobile_camera' : 'web');
      const res = await fetch(`${API}/documentos/importar`, { method: 'POST', headers, body: fd });
      const data = await readResponse(res);
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao importar documento.');
      setSelected(data);
      setContexto({});
      setForm((current) => ({
        ...current,
        arquivo: null,
        entidade_id: '',
        encaminhar: false,
        modulo_destino: data.modulo_destino || current.modulo_destino,
        tipo_destino: data.tipo_destino || current.tipo_destino,
      }));
      await loadData();
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Falha de conexão ao enviar o arquivo. Verifique o tamanho da imagem e tente novamente; se for foto do celular, use resolução menor ou tente novamente após alguns segundos.'
        : (err.message || 'Erro ao importar documento.'));
    } finally {
      setSaving(false);
    }
  };

  const routeSelected = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setError('');
    try {
      const headers = await jsonHeaders();
      const res = await fetch(`${API}/documentos/${selected.id}/encaminhar`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          modulo_destino: form.modulo_destino === 'automatico' ? selected.modulo_destino : form.modulo_destino,
	          tipo_destino: form.tipo_destino === 'automatico' ? selected.tipo_destino : form.tipo_destino,
	          entidade_id: form.entidade_id || null,
	          contexto: cleanContexto(),
	        }),
      });
      const data = await readResponse(res);
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao encaminhar documento.');
      setSelected(data.documento);
      await loadData();
    } catch (err) {
      setError(err.message || 'Erro ao encaminhar documento.');
    } finally {
      setSaving(false);
    }
  };

  const documentUrl = (id) => `${API}/documentos/${id}/download`;

  const preview = async (id, name) => {
    const headers = await authOnlyHeaders();
    try {
      await previewDocument(documentUrl(id), name || 'documento', headers);
    } catch (err) {
      setError(err.message || 'Erro ao abrir documento.');
    }
  };

  const download = async (id, name) => {
    const headers = await authOnlyHeaders();
    try {
      await downloadDocument(documentUrl(id), name || 'documento', headers);
    } catch (err) {
      setError(err.message || 'Erro ao baixar documento.');
    }
  };

  return (
    <FadeIn className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
              <h1 className="text-2xl font-bold text-white">Documentos/OCR</h1>
              <p className="mt-1 text-sm text-white/50">Entrada única para PDFs, imagens e anexos operacionais.</p>
        </div>
        <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>Atualizar</GlassButton>
      </div>

      {error && (
        <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
      )}

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <GlassCard className="p-5">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-white/80">Arquivo</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <GlassButton type="button" variant="secondary" icon={Camera} onClick={() => cameraInputRef.current?.click()}>Tirar foto</GlassButton>
                <GlassButton type="button" variant="outline" icon={FileText} onClick={() => fileInputRef.current?.click()}>Escolher arquivo</GlassButton>
              </div>
              <input
                ref={cameraInputRef}
                className="sr-only"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => selectFile(event.target.files?.[0] || null, true)}
              />
              <input
                ref={fileInputRef}
                className="sr-only"
                type="file"
                accept="image/*,.pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.txt,.csv,.md,.json,.xml"
                onChange={(event) => selectFile(event.target.files?.[0] || null, false)}
              />
            </div>
            {uploadInfo && <p className="-mt-2 text-xs text-sky-200/80">{uploadInfo}</p>}
            {form.captura_mobile && <p className="-mt-2 text-xs text-white/45">A foto será reduzida localmente antes do envio para tornar o OCR mais confiável. PDFs nunca são alterados.</p>}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-1">
              <GlassSelect
                label="Módulo"
                value={form.modulo_destino}
                onChange={(event) => updateForm('modulo_destino', event.target.value)}
                options={modules}
                placeholder=""
              />
              <GlassSelect
                label="Tipo"
                value={form.tipo_destino}
                onChange={(event) => updateForm('tipo_destino', event.target.value)}
                options={typeOptions}
                placeholder=""
              />
            </div>
            <GlassInput
              label="ID do cadastro"
              placeholder={contextHint(form.modulo_destino, form.tipo_destino)}
              value={form.entidade_id}
              onChange={(event) => updateForm('entidade_id', event.target.value)}
            />
            {cadastroConfig && (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-white/80">Selecionar {cadastroConfig.label}</p>
                    <p className="text-xs text-white/40">{loadingEntities ? 'Carregando cadastros...' : `${filteredEntityOptions.length} opções encontradas`}</p>
                  </div>
                  <GlassButton type="button" variant="ghost" icon={RefreshCw} onClick={loadEntityOptions} loading={loadingEntities}>Atualizar</GlassButton>
                </div>
                <div className="space-y-3">
                  <GlassInput
                    icon={Search}
                    value={entitySearch}
                    onChange={(event) => setEntitySearch(event.target.value)}
                    placeholder={`Buscar ${cadastroConfig.label.toLowerCase()}`}
                  />
                  <GlassSelect
                    value={form.entidade_id}
                    onChange={(event) => updateForm('entidade_id', event.target.value)}
                    options={filteredEntityOptions}
                    placeholder={loadingEntities ? 'Carregando...' : `Escolha ${cadastroConfig.label.toLowerCase()}`}
                  />
                </div>
              </div>
            )}
            {contextFields.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="mb-3">
                  <p className="text-sm font-medium text-white/80">Dados para encaminhamento</p>
                  <p className="text-xs text-white/40">Preencha apenas o que já estiver claro no documento.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-1">
                  {contextFields.map((field) => (
                    <GlassInput
                      key={field.key}
                      label={field.label}
                      type={field.type || 'text'}
                      step={field.step}
                      value={contexto[field.key] || ''}
                      onChange={(event) => updateContexto(field.key, event.target.value)}
                    />
                  ))}
                </div>
              </div>
            )}
            <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/70">
              <input
                type="checkbox"
                checked={form.encaminhar}
                onChange={(event) => updateForm('encaminhar', event.target.checked)}
                className="h-4 w-4 rounded border-white/20 bg-slate-900"
              />
              Encaminhar ao módulo após importar
            </label>
            <div className="flex gap-3">
              <GlassButton type="submit" icon={Upload} loading={saving} fullWidth>Importar</GlassButton>
              <GlassButton type="button" variant="outline" icon={Route} onClick={routeSelected} disabled={!selected?.id} loading={saving}>Encaminhar</GlassButton>
            </div>
          </form>

          {selected && (
            <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-white/90">{displayName(selected)}</p>
                  {selected.nome_original !== selected.nome_armazenado && (
                    <p className="mt-1 text-xs text-white/35">Original: {selected.nome_original}</p>
                  )}
                  <p className="mt-1 text-xs text-white/45">{selected.motor_extracao || '-'} · {selected.paginas || 0} pág.</p>
                </div>
                <span className={`rounded-full border px-2 py-1 text-xs ${statusClass(selected.status)}`}>{selected.status}</span>
              </div>
              {selected.contexto?.sugestao && (
                <div className="mt-3 rounded-lg border border-sky-400/20 bg-sky-500/10 p-3 text-sm text-sky-100">
                  <div className="font-medium">
                    Sugestão: {selected.contexto.sugestao.modulo_destino} / {selected.contexto.sugestao.tipo_destino}
                  </div>
                  <div className="mt-1 text-xs text-sky-100/70">
                    Confiança {selected.contexto.sugestao.confianca || 0}% · {selected.contexto.sugestao.motivo}
                  </div>
                </div>
              )}
              <p className="mt-3 line-clamp-6 text-sm text-white/60">{selected.texto_preview || selected.erro || 'Sem texto extraído.'}</p>
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-5">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Fila de documentos</h2>
              <p className="text-sm text-white/45">{filtered.length} registros</p>
            </div>
            <GlassInput
              icon={Search}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar"
              className="md:w-72"
            />
          </div>

          <div className="overflow-hidden rounded-xl border border-white/10">
            <div className="divide-y divide-white/10 md:hidden">
              {filtered.map((item) => (
                <div key={item.id} className="space-y-3 bg-white/[0.015] p-4">
                  <button
                    className="flex w-full items-start gap-3 text-left"
                    onClick={() => {
                      setSelected(item);
                      setForm((current) => ({ ...current, modulo_destino: item.modulo_destino || current.modulo_destino, tipo_destino: item.tipo_destino || current.tipo_destino, entidade_id: item.entidade_id || current.entidade_id }));
                    }}
                  >
                    <FileText className="mt-0.5 h-5 w-5 shrink-0 text-sky-300" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-white/85">{displayName(item)}</span>
                      <span className="mt-1 block text-xs text-white/45">{item.modulo_destino} / {item.tipo_destino} · {fmtDate(item.created_at)}</span>
                    </span>
                    <span className={`shrink-0 rounded-full border px-2 py-1 text-[11px] ${statusClass(item.status)}`}>{item.status}</span>
                  </button>
                  <div className="flex gap-2 pl-8">
                    <GlassButton size="sm" variant="ghost" icon={Eye} onClick={() => preview(item.id, displayName(item))}>Abrir</GlassButton>
                    <GlassButton size="sm" variant="ghost" icon={Download} onClick={() => download(item.id, displayName(item))}>Baixar</GlassButton>
                  </div>
                </div>
              ))}
              {!filtered.length && <p className="px-4 py-10 text-center text-sm text-white/45">Nenhum documento encontrado.</p>}
            </div>
            <div className="max-h-[640px] overflow-auto">
              <table className="hidden w-full text-left text-sm md:table">
                <thead className="sticky top-0 bg-slate-950/90 text-xs uppercase tracking-wide text-white/45">
                  <tr>
                    <th className="px-4 py-3">Arquivo</th>
                    <th className="px-4 py-3">Destino</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filtered.map((item) => (
                    <tr key={item.id} className="bg-white/[0.015] hover:bg-white/[0.04]">
                      <td className="px-4 py-3">
                        <button
                          className="flex items-start gap-3 text-left"
                          onClick={() => {
                            setSelected(item);
	                            setForm((current) => ({
	                              ...current,
	                              modulo_destino: item.modulo_destino || current.modulo_destino,
	                              tipo_destino: item.tipo_destino || current.tipo_destino,
	                              entidade_id: item.entidade_id || current.entidade_id,
	                            }));
                          }}
                        >
                          <FileText className="mt-0.5 h-4 w-4 text-sky-300" />
                          <span>
                            <span className="block max-w-[360px] truncate text-white/85">{displayName(item)}</span>
                            <span className="block max-w-[360px] truncate text-xs text-white/35">{item.nome_original}</span>
                            <span className="block text-xs text-white/40">{fmtBytes(item.tamanho_bytes)} · {item.motor_extracao || '-'}</span>
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-white/60">{item.modulo_destino} / {item.tipo_destino}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full border px-2 py-1 text-xs ${statusClass(item.status)}`}>{item.status}</span>
                      </td>
                      <td className="px-4 py-3 text-white/50">{fmtDate(item.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <GlassButton size="sm" variant="ghost" icon={Eye} onClick={() => preview(item.id, displayName(item))}>Abrir</GlassButton>
                          <GlassButton size="sm" variant="ghost" icon={Download} onClick={() => download(item.id, displayName(item))}>Baixar</GlassButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr>
                      <td colSpan="5" className="px-4 py-10 text-center text-white/45">Nenhum documento encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </GlassCard>
      </div>
    </FadeIn>
  );
};

export default DocumentosOCR;
